import React, { useState } from "react";

/**
 * LensPicker - Simplified version for ZenPlayground
 * Features:
 *  - Dropdown selection with categories
 *  - Preview pane with example output
 *  - Zen theme integration
 */

// -------------------- Types --------------------
export type Lens = {
  id: string;
  title: string;
  short?: string;
  category: "Standard" | "Learning Style" | "Language" | "Domain";
  tags?: string[];
  exampleIn?: string;
  preview?: string;
};

export type LensPickerProps = {
  lenses: Lens[];
  value?: string;
  onChange?: (lensId: string) => void;
  className?: string;
  compact?: boolean;
};

// -------------------- Main Component --------------------
export const LensPicker: React.FC<LensPickerProps> = ({
  lenses,
  value,
  onChange,
  className,
  compact = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const current = lenses.find((l) => l.id === value) || lenses[0];

  const handleSelect = (lensId: string) => {
    onChange?.(lensId);
    setIsOpen(false);
  };

  // Group lenses by category
  const byCategory = lenses.reduce<Record<string, Lens[]>>((acc, lens) => {
    if (!acc[lens.category]) {
      acc[lens.category] = [];
    }
    acc[lens.category].push(lens);
    return acc;
  }, {});

  if (compact) {
    return (
      <div className={`lens-picker-compact ${className || ''}`}>
        {/* Dropdown Button */}
        <div className="lens-picker-dropdown">
          <button 
            className="lens-picker-button"
            onClick={() => setIsOpen(!isOpen)}
            type="button"
          >
            <span className="lens-picker-current-text">
              {current?.title?.split(' — ')[0] || "Choose Lens"}
            </span>
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="lens-picker-menu">
              {Object.entries(byCategory).map(([category, categoryLenses]) => (
                <div key={category} className="lens-picker-category">
                  <div className="lens-picker-category-header">{category}</div>
                  {categoryLenses.map((lens) => (
                    <button
                      key={lens.id}
                      className={`lens-picker-option ${value === lens.id ? 'selected' : ''}`}
                      onClick={() => handleSelect(lens.id)}
                      type="button"
                    >
                      <div className="lens-picker-option-main">
                        <div className="lens-picker-option-title">{lens.title}</div>
                        {lens.short && (
                          <div className="lens-picker-option-short">{lens.short}</div>
                        )}
                      </div>
                      {lens.tags && (
                        <div className="lens-picker-tags">
                          {lens.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="lens-picker-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>


        {/* Backdrop for closing dropdown */}
        {isOpen && (
          <div 
            className="lens-picker-backdrop" 
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`lens-picker-full ${className || ''}`}>
      {/* Full mode not implemented for simplicity */}
      <div>Full mode not implemented</div>
    </div>
  );
};